// =============================================================================
// Rolodex — faithful port of IMS New_Rolodex_Dashboard + New_Add_Entry /
// New_View_Entry. Searchable contact directory (vendors, suppliers, trades,
// references) with add/edit/delete. Backed by rolodex_contacts.
// =============================================================================
import { useState } from 'react'
import { Plus, X, Trash2 } from 'lucide-react'
import { listRolodexContacts, createRolodexContact, updateRolodexContact, deleteRolodexContact } from '../services/data'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, Button } from '../components/shared/ui'
import { useServerList } from '../lib/list'
import type { RolodexContact } from '../types'
import { toast } from 'react-toastify'

const PAGE_SIZE = 25
const blank = {
  last_name: '', first_name: '', company: '', position: '', division: '',
  address: '', address2: '', city: '', state: '', zip: '',
  phone: '', bus_phone: '', other_phone: '', fax_number: '', notes: '',
}

export default function Rolodex() {
  const [edit, setEdit] = useState<RolodexContact | 'new' | null>(null)
  const { rows, count, page, search, sort, loading, setPage, setSearch, toggleSort, reload } =
    useServerList<RolodexContact>(listRolodexContacts, { pageSize: PAGE_SIZE, defaultSort: { column: 'company', dir: 'asc' } })

  return (
    <div>
      <PageHeader title="Rolodex" subtitle={`${count.toLocaleString()} contacts`}
        actions={<Button onClick={() => setEdit('new')}><Plus size={16} /> New Contact</Button>} />
      <Card className="p-4">
        <div className="mb-4">
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, company, city, phone…" />
        </div>
        {loading ? <Spinner label="Loading contacts…" /> : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              onRowClick={c => setEdit(c)}
              columns={[
                { key: 'company', header: 'Company', render: c => <span className="font-medium text-gray-900">{c.company || '—'}</span>, sortKey: 'company' },
                { key: 'name', header: 'Name', render: c => [c.first_name, c.last_name].filter(Boolean).join(' ') || '—', sortKey: 'last_name' },
                { key: 'position', header: 'Position', render: c => c.position || '—', sortKey: 'position' },
                { key: 'phone', header: 'Phone', render: c => c.phone || c.bus_phone || '—', sortKey: 'phone' },
                { key: 'city', header: 'City', render: c => [c.city, c.state].filter(Boolean).join(', ') || '—', sortKey: 'city' },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>
      {edit && <ContactForm contact={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload() }} />}
    </div>
  )
}

function ContactForm({ contact, onClose, onSaved }: { contact: RolodexContact | null; onClose: () => void; onSaved: () => void }) {
  const init = contact
    ? { ...blank, ...Object.fromEntries(Object.keys(blank).map(k => [k, (contact as Record<string, unknown>)[k] ?? ''])) }
    : { ...blank }
  const [f, setF] = useState<typeof blank>(init as typeof blank)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const set = (k: keyof typeof blank, v: string) => setF(p => ({ ...p, [k]: v }))

  const save = async () => {
    if (!f.company.trim() && !f.last_name.trim() && !f.first_name.trim()) {
      toast.error('Enter a company or contact name'); return
    }
    setSaving(true)
    try {
      if (contact) await updateRolodexContact(contact.id, f as Partial<RolodexContact>)
      else await createRolodexContact(f as Partial<RolodexContact>)
      toast.success('Contact saved!'); onSaved()
    } catch (e) { console.error(e); toast.error('Failed to save contact') } finally { setSaving(false) }
  }

  const remove = async () => {
    if (!contact) return
    if (!window.confirm('Delete this contact? This cannot be undone.')) return
    setDeleting(true)
    try {
      await deleteRolodexContact(contact.id)
      toast.success('Contact deleted'); onSaved()
    } catch (e) { console.error(e); toast.error('Failed to delete contact') } finally { setDeleting(false) }
  }

  const F = (label: string, k: keyof typeof blank, cls = '') => (
    <div className={cls}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input value={f[k]} onChange={e => set(k, e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{contact ? 'Edit Contact' : 'New Contact'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto grid grid-cols-2 md:grid-cols-3 gap-3">
          {F('Company', 'company', 'col-span-2 md:col-span-2')}
          {F('Position', 'position')}
          {F('First Name', 'first_name')}
          {F('Last Name', 'last_name')}
          {F('Division', 'division')}
          {F('Address', 'address', 'col-span-2 md:col-span-3')}
          {F('Address 2', 'address2', 'col-span-2 md:col-span-3')}
          {F('City', 'city')}
          {F('State', 'state')}
          {F('Zip', 'zip')}
          {F('Phone', 'phone')}
          {F('Business Phone', 'bus_phone')}
          {F('Other Phone', 'other_phone')}
          {F('Fax', 'fax_number')}
          <div className="col-span-2 md:col-span-3">
            <label className="text-xs font-medium text-gray-500">Notes</label>
            <textarea value={f.notes} onChange={e => set('notes', e.target.value)} rows={3}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-200">
          <div>
            {contact && (
              <Button variant="ghost" onClick={remove} disabled={deleting} className="text-red-600">
                <Trash2 size={16} /> {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>Close</Button>
            <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
